import dayjs from 'dayjs'
import { ExternalLink, Scroll } from 'lucide-react'
import type { ActionTrailEvent, Ticket, TicketDetail, ViewingAs } from '@/types/ticket'
import { ActionTrail } from './ActionTrail'

const CONTEXT_LABELS: Record<string, string> = {
  pageUrl: 'Page',
  userAgent: 'Browser',
  appVersion: 'App version',
  betterStackTraceId: 'BetterStack ID',
  source: 'Source',
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[68px_1fr] items-baseline gap-2 text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-foreground break-words min-w-0">{children}</span>
    </div>
  )
}

function ContextValue({ value }: { value: unknown }) {
  if (value == null) return <span className="text-muted-foreground">—</span>
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground italic text-xs">empty</span>
    return <span className="font-mono text-xs">{JSON.stringify(value)}</span>
  }
  if (typeof value === 'object') {
    return <span className="font-mono text-xs">{JSON.stringify(value)}</span>
  }
  return <span className="break-all">{String(value)}</span>
}

export function ContextPanel({ ticket, links, viewingAs }: { ticket: Ticket; links?: TicketDetail['links']; viewingAs?: ViewingAs }) {
  const ctx = (ticket.context ?? {}) as Record<string, unknown>
  const actionTrail: ActionTrailEvent[] = Array.isArray(ctx.actionTrail)
    ? (ctx.actionTrail as ActionTrailEvent[])
    : []
  // Render every other context field in the generic panel; the trail has its own UI.
  const ctxEntries = Object.entries(ctx).filter(
    ([k, v]) => k !== 'actionTrail' && v !== undefined && v !== null && v !== '',
  )

  return (
    <div className="divide-y divide-border">
      <section className="px-5 py-4 space-y-4">
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-2.5">
            Details
          </h3>
          <div className="space-y-2">
            {ticket.creator && (
              <Row label="Reporter">
                {ticket.creator.name ?? ticket.creator.email ?? '—'}
              </Row>
            )}
            <Row label="Created">{dayjs(ticket.createdAt).format('MMM D, YYYY · HH:mm')}</Row>
            {ticket.closedAt && (
              <Row label="Closed">{dayjs(ticket.closedAt).format('MMM D, YYYY · HH:mm')}</Row>
            )}
          </div>
        </div>

        {ctxEntries.length > 0 && (
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-2.5">
              Page context
            </h3>
            <div className="space-y-2">
              {ctxEntries.map(([k, v]) => (
                <Row key={k} label={CONTEXT_LABELS[k] ?? k}>
                  <ContextValue value={v} />
                </Row>
              ))}
            </div>
          </div>
        )}

        {links?.betterStackLogs && (
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-2.5">
              Tools
            </h3>
            <a
              href={links.betterStackLogs}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2.5 py-1.5 text-xs hover:bg-accent transition-colors"
            >
              <Scroll className="h-3.5 w-3.5 text-muted-foreground" />
              Open server logs
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </a>
          </div>
        )}
      </section>

      {/* Staff-only trail; the API strips it under the view-as lens, so skip rendering it there too. */}
      {!viewingAs && (
        <ActionTrail
          ticketId={ticket._id}
          ticketCreatedAt={ticket.createdAt}
          snapshot={actionTrail}
        />
      )}
    </div>
  )
}
